import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "fr-cookie-consent";
const ADSENSE_CLIENT = "ca-pub-1050440072846932";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

export function hasMarketingConsent() {
  return getConsent()?.marketing === true;
}

export function onConsentChange(cb) {
  window.addEventListener("fr-consent-changed", cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener("fr-consent-changed", cb);
    window.removeEventListener("storage", cb);
  };
}

// Script AdSense iniettato in <head> SOLO dopo consenso marketing (blocco preventivo GDPR).
// async => non blocca il rendering della pagina.
function loadAdsense() {
  if (document.querySelector("script[data-adsense]")) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  s.crossOrigin = "anonymous";
  s.dataset.adsense = "1";
  document.head.appendChild(s);
}

function saveConsent(marketing) {
  localStorage.setItem(KEY, JSON.stringify({ necessary: true, marketing, ts: new Date().toISOString() }));
  window.dispatchEvent(new Event("fr-consent-changed"));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c) {
      setVisible(true);
    } else if (c.marketing) {
      loadAdsense();
    }
    const reopen = () => setVisible(true);
    window.addEventListener("fr-open-cookie-preferences", reopen);
    return () => window.removeEventListener("fr-open-cookie-preferences", reopen);
  }, []);

  if (!visible) return null;

  const choose = (marketing) => {
    saveConsent(marketing);
    if (marketing) loadAdsense();
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-24 lg:bottom-4 left-3 right-3 md:left-auto md:right-4 md:max-w-md z-[90] rounded-2xl border border-[#3F3F4C] bg-[#1A1A1E]/95 backdrop-blur-xl p-5 shadow-2xl"
    >
      <div className="flex items-start gap-3">
        <Cookie className="w-6 h-6 text-[#FFB703] shrink-0 mt-0.5" />
        <div>
          <p className="font-racing font-bold text-sm uppercase tracking-wide text-white">Cookie & Privacy</p>
          <p className="text-xs text-gray-400 font-body-sport leading-relaxed mt-1.5">
            Usiamo cookie tecnici per far funzionare FantasyRace. Solo con il tuo consenso caricheremo annunci
            pubblicitari (Google AdSense). Leggi la{" "}
            <Link to="/cookie-policy" data-testid="cookie-banner-policy-link" className="text-[#FFB703] hover:underline">
              Cookie Policy
            </Link>{" "}
            e la{" "}
            <Link to="/privacy" className="text-[#FFB703] hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
          <div className="flex gap-2 mt-3.5">
            <button
              data-testid="cookie-accept-all"
              onClick={() => choose(true)}
              className="flex-1 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors py-2.5 font-racing font-bold text-xs tracking-widest text-white uppercase"
            >
              Accetta tutti
            </button>
            <button
              data-testid="cookie-accept-necessary"
              onClick={() => choose(false)}
              className="flex-1 rounded-lg border border-[#3F3F4C] hover:border-gray-500 transition-colors py-2.5 font-racing font-bold text-xs tracking-widest text-gray-300 uppercase"
            >
              Solo necessari
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
